import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  Warehouse,
  Car,
  Store,
  ShieldAlert,
  Network,
  Wrench,
  HardDrive,
  Monitor,
} from 'lucide-react';

import './ProductDetail.css';
import './SecurityCamera.css';

const ImageWithFallback = ({ src, fallback, alt, ...props }) => {
  const handleError = (event) => {
    if (!fallback || event.currentTarget.dataset.fallbackApplied === 'true') return;
    event.currentTarget.dataset.fallbackApplied = 'true';
    event.currentTarget.src = fallback;
  };

  return <img src={src} alt={alt} onError={handleError} {...props} />;
};

const SecurityCamera = () => {
  const heroFeatures = [
    {
      Icon: ShieldAlert,
      title: '安心のセキュリティ対策',
      text: '用途に合わせた防犯・録画構成',
    },
    {
      Icon: Wrench,
      title: '現場に合わせたご提案',
      text: '設置場所や配線条件を確認',
    },
    {
      Icon: Network,
      title: '設置から運用まで',
      text: '機器選定・設定・遠隔確認をサポート',
    },
    {
      Icon: Building2,
      title: '法人・大規模案件',
      text: '複数拠点・大型施設にも対応',
    },
  ];

  /**
   * TOP page = category gateway.
   * Individual products are shown on /services/security-camera/lineup.
   */
  const locations = [
    {
      id: 'shop-restaurant',
      label: '店舗・飲食店',
      Icon: Store,
      desc: 'レジ・売場・出入口の確認に',
      image: '/images/usecase-shop.png',
      fallbackImage: '/images/usecase-office.jpg',
      objectPosition: 'center',
      link: '/services/security-camera/lineup?location=shop',
    },
    {
      id: 'office',
      label: 'オフィス・事務所',
      Icon: Building2,
      desc: '受付・出入口・共用部の監視に',
      image: '/images/usecase-office.jpg',
      fallbackImage: '/images/usecase-shop.jpg',
      objectPosition: 'center',
      link: '/services/security-camera/lineup?location=office',
    },
    {
      id: 'parking',
      label: '駐車場・建物外周',
      Icon: Car,
      desc: '車両の出入りや夜間の監視に',
      image: '/images/usecase-parking.jpg',
      fallbackImage: '/images/usecase-parking-coinparking.png',
      objectPosition: 'center',
      link: '/services/security-camera/lineup?location=parking',
    },
    {
      id: 'warehouse',
      label: '倉庫・工場',
      Icon: Warehouse,
      desc: '搬入口・構内・作業エリアの確認に',
      image: '/images/usecase-warehouse.jpg',
      fallbackImage: '/images/ip-camera.png',
      objectPosition: 'center',
      link: '/services/security-camera/lineup?location=warehouse',
    },
  ];

  const featuredProducts = [
    {
      id: 'poe-dome',
      label: 'DOME',
      name: 'PoEドームカメラ',
      description:
        '店舗・事務所などの屋内監視に。天井設置に適した、すっきりとした定番タイプです。',
      image: '/images/product-dome.jpg',
      fallbackImage: '/images/ip-camera.png',
      link: '/services/security-camera/lineup?type=dome',
      tags: ['PoE', '屋内向け', '定番タイプ'],
    },
    {
      id: 'poe-bullet',
      label: 'BULLET',
      name: 'PoEバレットカメラ',
      description:
        '駐車場・建物外周などの屋外監視に。監視方向が分かりやすく、幅広い場所に設置できます。',
      image: '/images/product-bullet.jpg',
      fallbackImage: '/images/ip-camera.png',
      link: '/services/security-camera/lineup?type=bullet',
      tags: ['PoE', '屋外向け', '外周監視'],
    },
    {
      id: '4k-poe',
      label: '4K / 8MP',
      name: '4K高画質カメラ',
      description:
        '映像の細部まで確認したい場所に。高画質を重視する店舗・施設・設備監視に適しています。',
      image: '/images/product-4k.jpg',
      fallbackImage: '/images/super-colorvu.png',
      link: '/services/security-camera/lineup?resolution=8MP',
      tags: ['8MP', '4K', '高画質'],
    },
    {
      id: 'ptz',
      label: 'PTZ',
      name: 'PTZカメラ',
      description:
        '広い範囲を確認したい場所に。首振りやズームを活用して、広範囲の監視に対応します。',
      image: '/images/product-ptz.jpg',
      fallbackImage: '/images/ptz-camera.png',
      link: '/services/security-camera/lineup?type=ptz',
      tags: ['広範囲', '光学ズーム', '方向調整'],
    },
    {
      id: 'night-vision',
      label: 'NIGHT VISION',
      name: '夜間対応カメラ',
      description:
        '夜間や照明の少ない場所の監視に。駐車場・倉庫・建物外周などに適したタイプです。',
      image: '/images/product-night.png',
      fallbackImage: '/images/ir-night-vision.png',
      link: '/services/security-camera/lineup?feature=night',
      tags: ['夜間監視', '赤外線', '屋外対応'],
    },
    {
      id: 'panoramic-180',
      label: 'PANORAMIC',
      name: '広角・180°カメラ',
      description:
        '広いエリアを一度に確認したい場所に。死角を抑えながら広範囲を見渡せます。',
      image: '/images/product-180.png',
      fallbackImage: '/images/ip-camera.png',
      link: '/services/security-camera/lineup?type=panoramic',
      tags: ['広角', '180°', '広範囲監視'],
    },
  ];

  const recordingSystems = [
    {
      id: '4-camera-set',
      label: '4 CAMERA SET',
      name: '4台PoE録画セット',
      description:
        '小規模店舗・事務所などに。カメラ4台と録画機を組み合わせた、導入しやすい基本構成です。',
      image: '/images/set-4.jpg',
      fallbackImage: '/images/ip-camera.png',
      tags: ['4台構成', 'PoE', '録画機セット'],
      link: '/services/security-camera/nvr-recorder',
      cta: '詳しく見る',
    },
    {
      id: '8-camera-set',
      label: '8 CAMERA SET',
      name: '8台PoE録画セット',
      description:
        '倉庫・工場・中規模施設など、複数エリアをまとめて確認したい場合に適した構成です。',
      image: '/images/set-8.jpg',
      fallbackImage: '/images/ip-camera.png',
      tags: ['8台構成', 'PoE', '中規模施設向け'],
      link: '/services/security-camera/nvr-recorder',
      cta: '詳しく見る',
    },
    {
      id: 'nvr-recorder',
      label: 'NVR RECORDER',
      name: 'NVR録画機',
      description:
        '既存カメラの録画環境の見直しや、録画機の入替・増設をご検討の方に。',
      image: '/images/nvr-recorder.png',
      Icon: HardDrive,
      tags: ['録画機単体', '入替・増設', '既存設備'],
      link: '/services/security-camera/nvr-recorder',
      cta: '詳しく見る',
    },
    {
      id: 'large-system',
      label: 'BUSINESS SYSTEM',
      name: '大規模・複数拠点構成',
      description:
        '多台数のカメラや複数拠点の監視など、施設規模に合わせて個別に構成をご提案します。',
      image: '/images/business-system.png',
      Icon: Network,
      tags: ['多台数', '複数拠点', '個別提案'],
      link: '/contact',
      cta: '構成について相談する',
    },
  ];

  const guideLinks = [
    {
      Icon: Monitor,
      title: 'はじめての方へ',
      text: '防犯カメラ選びの基本をご案内します。',
      to: '/contact',
    },
    {
      Icon: ShieldAlert,
      title: 'よくあるご質問',
      text: '導入前によくあるご質問をご確認いただけます。',
      to: '/contact',
    },
    {
      Icon: Wrench,
      title: 'サポート・アフターサービス',
      text: '設置後の運用や追加のご相談にも対応します。',
      to: '/contact',
    },
  ];

  useEffect(() => {
    const previousTitle = document.title;
    const description =
      '店舗・飲食店、オフィス・事務所、駐車場・建物外周、倉庫・工場などに対応する防犯カメラ・録画設備。カメラ単品、PoE録画システム、設置工事まで用途に合わせてご提案します。';

    let metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? null;
    const createdMetaDescription = !metaDescription;

    document.title = '防犯カメラ・録画設備｜株式会社エイチエス';

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute('content', description);

    return () => {
      document.title = previousTitle;

      if (createdMetaDescription) {
        metaDescription.remove();
      } else if (previousDescription !== null) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const renderSectionHeading = ({ english, title, description }) => (
    <header className="sc-section-heading">
      <p className="sc-section-kicker">{english}</p>
      <h2>{title}</h2>
      <span className="sc-section-rule" aria-hidden="true" />
      {description ? <p className="sc-section-description">{description}</p> : null}
    </header>
  );

  return (
    <main
      className="product-detail-page sc-refined"
      id="page-top"
      data-testid="security-camera-page"
    >
      {/* HERO */}
      <section
        className="sc-hero"
        aria-labelledby="security-camera-title"
        style={{ '--sc-hero-bg': "url('/images/hero-background.png')" }}
      >
        <div className="container sc-hero-layout">
          <div className="sc-hero-copy">
            <Link to="/products" className="sc-hero-back">
              <ArrowLeft size={13} strokeWidth={1.7} aria-hidden="true" />
              製品・サービス一覧へ戻る
            </Link>

            <p className="sc-hero-eyebrow">
              <span className="sc-hero-eyebrow-dot" aria-hidden="true" />
              SECURITY CAMERA
            </p>

            <h1 id="security-camera-title">
              防犯カメラを、
              <br />
              1台から。
            </h1>

            <span className="sc-hero-rule" aria-hidden="true" />

            <p className="sc-hero-lead">
              店舗・事務所・倉庫・駐車場など、さまざまな設置場所に対応。
              <br />
              カメラ単品、録画セット、設置相談までご案内します。
            </p>

            <div className="sc-hero-actions">
              <a className="sc-hero-button" href="#series">
                カメラを選ぶ
              </a>
              <a
                className="sc-hero-button sc-hero-button-secondary"
                href="#security-contact"
              >
                設置・法人相談
              </a>
            </div>

            <div className="sc-hero-features">
              {heroFeatures.map(({ Icon, title, text }) => (
                <div className="sc-hero-feature" key={title}>
                  <Icon size={22} strokeWidth={1.7} aria-hidden="true" />
                  <div>
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <figure className="sc-hero-visual">
            <ImageWithFallback
              src="/images/hero-approved.jpg"
              fallback="/images/security-camera-hero.png"
              alt="HS 防犯カメラ・録画設備"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </figure>
        </div>
      </section>

      {/* INSTALLATION LOCATION */}
      <section className="sc-section" id="usage" data-testid="section-location">
        <div className="container">
          {renderSectionHeading({
            english: 'LOCATION',
            title: '設置場所から選ぶ',
            description:
              '設置場所やご利用シーンに合わせて、適したカメラ・録画システムをご案内します。',
          })}

          <div className="sc-usage-grid">
            {locations.map(
              ({ id, label, Icon, desc, image, fallbackImage, objectPosition, link }) => (
                <Link
                  key={id}
                  to={link}
                  className="sc-usage-card"
                  aria-label={`${label}向けの防犯カメラを見る`}
                >
                  <ImageWithFallback
                    className="sc-usage-photo"
                    src={image}
                    fallback={fallbackImage}
                    alt=""
                    loading="lazy"
                    style={{ objectPosition }}
                  />

                  <span className="sc-usage-overlay" aria-hidden="true" />

                  <span className="sc-usage-icon">
                    <Icon size={18} strokeWidth={1.7} aria-hidden="true" />
                  </span>

                  <span className="sc-usage-copy">
                    <strong className="sc-usage-name">{label}</strong>
                    <span className="sc-usage-desc">{desc}</span>
                  </span>

                  <ArrowRight
                    className="sc-usage-arrow"
                    size={15}
                    strokeWidth={1.7}
                    aria-hidden="true"
                  />
                </Link>
              ),
            )}
          </div>
        </div>
      </section>

      {/* CAMERA SELECTION */}
      <section
        className="sc-section sc-section-pale"
        id="series"
        data-testid="section-series"
      >
        <div className="container">
          {renderSectionHeading({
            english: 'SELECT CAMERA',
            title: 'カメラを選ぶ',
            description:
              '設置場所や監視目的に合わせて、カメラタイプからお選びいただけます。',
          })}

          <div className="sc-series-grid">
            {featuredProducts.map((item) => (
              <Link
                key={item.id}
                to={item.link}
                className="sc-series-card"
                data-testid={`camera-category-${item.id}`}
                aria-label={`${item.name}を見る`}
              >
                <div className="sc-series-image">
                  <ImageWithFallback
                    src={item.image}
                    fallback={item.fallbackImage}
                    alt={item.name}
                    loading="lazy"
                  />
                </div>

                <div className="sc-series-body">
                  <span className="sc-series-label">{item.label}</span>
                  <h3 className="sc-series-name">{item.name}</h3>
                  <p className="sc-series-desc">{item.description}</p>

                  <div className="sc-tag-list" aria-label="主な特長">
                    {item.tags.map((tag) => (
                      <span className="sc-tag" key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>

                  <span className="sc-series-link">
                    対応製品を見る
                    <ArrowRight size={15} strokeWidth={1.7} aria-hidden="true" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="sc-lineup-link-wrap">
            <Link
              to="/services/security-camera/lineup"
              className="sc-lineup-link"
            >
              防犯カメラのラインアップを見る
              <ArrowRight size={16} strokeWidth={1.7} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* RECORDING SYSTEMS */}
      <section
        className="sc-section"
        id="presets"
        data-testid="section-recording-systems"
      >
        <div className="container">
          {renderSectionHeading({
            english: 'RECORDING SYSTEM',
            title: '録画システム',
            description:
              'カメラ台数や設置規模に合わせて、録画機を含むシステム構成をご提案します。',
          })}

          <div className="sc-preset-grid">
            {recordingSystems.map((system) => {
              const VisualIcon = system.Icon;

              return (
                <article
                  key={system.id}
                  className="sc-preset-card"
                  data-testid={`recording-system-${system.id}`}
                >
                  <div
                    className={`sc-system-visual ${
                      system.image ? 'sc-system-visual--image' : 'sc-system-visual--icon'
                    }`}
                  >
                    {system.image ? (
                      <ImageWithFallback
                        src={system.image}
                        fallback={system.fallbackImage}
                        alt={system.name}
                        loading="lazy"
                      />
                    ) : (
                      <VisualIcon size={48} strokeWidth={1.45} aria-hidden="true" />
                    )}
                  </div>

                  <div className="sc-preset-body">
                    <span className="sc-series-label">{system.label}</span>
                    <h3>{system.name}</h3>
                    <p>{system.description}</p>

                    <ul className="sc-tag-list" aria-label="主なポイント">
                      {system.tags.map((tag) => (
                        <li className="sc-tag" key={tag}>
                          {tag}
                        </li>
                      ))}
                    </ul>

                    <Link to={system.link} className="sc-preset-link">
                      {system.cta}
                      <ArrowRight size={15} strokeWidth={1.7} aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CORPORATE / INSTALLATION CONSULTATION */}
      <section className="sc-contact" id="security-contact">
        <div className="container sc-contact-panel">
          <div>
            <p className="sc-page-eyebrow">INSTALLATION & BUSINESS</p>
            <h2>設置工事・法人向けのご相談</h2>
            <p>
              現地調査・機器選定から設置工事、設定、導入後のサポートまで。
              法人・大規模案件もお気軽にご相談ください。
            </p>

            <Link to="/contact">お問い合わせ・ご相談はこちら</Link>
          </div>

          <div className="sc-contact-summary">
            <span>対応内容</span>
            <strong>現地調査・機器選定・設置工事・設定</strong>
            <span>対象</span>
            <strong>店舗・事務所・工場・倉庫・駐車場・各種施設</strong>
            <span>システム</span>
            <strong>単体カメラから複数台・大規模構成まで</strong>
          </div>
        </div>
      </section>

      {/* GUIDE */}
      <section className="sc-guide-section" aria-labelledby="security-guide-title">
        <div className="container">
          <p className="sc-section-kicker">GUIDE</p>
          <h2 id="security-guide-title">ご利用ガイド</h2>

          <div className="sc-guide-grid">
            {guideLinks.map(({ Icon, title, text, to }) => (
              <Link key={title} to={to} className="sc-guide-card">
                <span className="sc-guide-icon">
                  <Icon size={19} strokeWidth={1.7} aria-hidden="true" />
                </span>

                <span className="sc-guide-copy">
                  <strong>{title}</strong>
                  <small>{text}</small>
                </span>

                <ArrowRight
                  className="sc-guide-arrow"
                  size={15}
                  strokeWidth={1.7}
                  aria-hidden="true"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default SecurityCamera;
